import { useEffect } from 'react';

const normalizeKey = (value) => String(value || '').trim().toLowerCase();

const isEditableTarget = (target) => {
  if (!target || typeof target !== 'object') return false;
  if (target.isContentEditable) return true;

  const tagName = String(target.tagName || '').toLowerCase();
  if (['input', 'textarea', 'select'].includes(tagName)) return true;

  return typeof target.closest === 'function'
    ? Boolean(target.closest('[contenteditable="true"], input, textarea, select'))
    : false;
};

const matchesShortcut = (event, shortcut) => {
  const expectedKey = normalizeKey(shortcut.key);
  const actualKey = normalizeKey(event.key);

  if (!expectedKey || actualKey !== expectedKey) return false;
  if (Boolean(shortcut.ctrlKey) !== Boolean(event.ctrlKey)) return false;
  if (Boolean(shortcut.shiftKey) !== Boolean(event.shiftKey)) return false;
  if (Boolean(shortcut.altKey) !== Boolean(event.altKey)) return false;
  if (Boolean(shortcut.metaKey) !== Boolean(event.metaKey)) return false;

  return true;
};

export const useKeyboardShortcuts = (shortcuts = [], options = {}) => {
  const { enabled = true } = options;

  useEffect(() => {
    if (!enabled || !Array.isArray(shortcuts) || shortcuts.length === 0) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.repeat) return;

      for (const shortcut of shortcuts) {
        if (!shortcut || typeof shortcut.handler !== 'function' || shortcut.disabled) continue;
        if (!shortcut.allowInInput && isEditableTarget(event.target)) continue;
        if (!matchesShortcut(event, shortcut)) continue;

        if (shortcut.preventDefault !== false) {
          event.preventDefault();
          event.stopPropagation();
        }

        shortcut.handler(event);
        break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [enabled, shortcuts]);
};

export default useKeyboardShortcuts;
